import { AsyncPipe, DatePipe } from "@angular/common";
import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import _ from "lodash";
import { firstValueFrom, Observable, Subject } from "rxjs";
import { filter, takeUntil, tap, map } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, VirkeskopBlobStorageTyp } from "~app/common/models/constants";
import { DateUtils } from "~app/common/utils/date-utils";
import { AuthenticationService } from "../../../../common/services/authentication/authentication.service";
import { ObjectUtils } from "../../../../common/utils/object-utils";
import { ValidationMessageRowComponent } from "../../../../common/validation/validation-message-row/validation-message-row.component";
import { VirkeskopService } from "../../../virkeskop/services/virkeskop.service";
import { KontraktDokumentComponent } from "../../kontrakt/kontrakt-dokument/kontrakt-dokument.component";
import { Atgard, VirkeskopsatgardKompatibilitet } from "../../models/atgard-models";
import { AtgardNaturvardesbedomningDTO, NaturvardesbedomningKalla, NaturvardesomradeDTO } from "../../models/naturvardesbedomning-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvtalService } from "../../services/avtal.service";
import { KontraktDokumentService } from "../../services/kontrakt-dokument.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { NvbMediatorService } from "../../services/nvb-mediator.service";
import { AtgardNvbLeveransvirkeComponent } from "./atgard-nvb-leveransvirke/atgard-nvb-leveransvirke.component";
import { ForenkladRiskbedomningComponent } from "./forms/forenklad-riskbedomning/forenklad-riskbedomning.component";
import { NVBMellerstaSverigeComponent } from "./forms/nvbmellersta-sverige/nvbmellersta-sverige.component";
import { NVBNorraSverigeComponent } from "./forms/nvbnorra-sverige/nvbnorra-sverige.component";
import { NVBSodraSverigeComponent } from "./forms/nvbsodra-sverige/nvbsodra-sverige.component";
import { canEdit } from "~app/role-permissions";
type NaturvardesbedomningForm = FormGroup<{
    nvbKravsEj: FormControl<boolean | null>;
    nvbSource: FormControl<NaturvardesbedomningKalla | null>;
}>;

@Component({
    selector: "rask-atgard-nvb",
    templateUrl: "./atgard-nvb.component.html",
    styleUrls: ["./atgard-nvb.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ValidationMessageRowComponent,
        KontraktDokumentComponent,
        NVBNorraSverigeComponent,
        NVBMellerstaSverigeComponent,
        NVBSodraSverigeComponent,
        FormsModule,
        ReactiveFormsModule,
        RadiobuttonGroupComponent,
        ButtonComponent,
        DatePipe,
        ForenkladRiskbedomningComponent,
        AtgardNvbLeveransvirkeComponent,
        AsyncPipe
    ]
})
export class AtgardNvbComponent implements OnInit, OnDestroy {
    readonly mellerstaSverige = "473eb0bc-7b56-4dd2-9e2a-7084c497278d";
    readonly norraSverige = "c0e623d6-2974-4d34-907b-4fa397f7494c";
    readonly sodraSverige = "7503a78a-4032-44d2-9d4f-4f6c83d8136e";

    naturvardesbedomning: AtgardNaturvardesbedomningDTO;
    naturvardesomradeGuid: string;

    protected blobStorageDokumentTypNvbBilaga = VirkeskopBlobStorageTyp.NvbBilaga;
    protected form: NaturvardesbedomningForm;
    protected kontraktReadonly$: Observable<boolean>;
    protected naturvardesomradeNotFound = false;
    protected requireAtLeastOneFile = false;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly authService = inject(AuthenticationService);
    private readonly avtalService = inject(AvtalService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngOnDestroy$ = new Subject<void>();
    private readonly nvbMediatorService = inject(NvbMediatorService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly virkeskopService = inject(VirkeskopService);

    private _atgard: Atgard;
    private persisted = false;

    private readonly canEdit = canEdit();

    constructor() {
        this.atgardMediatorService.atgard$.pipe(
            filter(atgard => atgard.id > 0),
            takeUntil(this.ngOnDestroy$)
        ).subscribe(atgard => {
            this._atgard = atgard;
            this.init();
        });

        this.form = new FormGroup({
            nvbKravsEj: new FormControl({
                value: null
            }, Validators.required),
            nvbSource: new FormControl({
                value: null
            }, Validators.required)
        });

        this.form.valueChanges.pipe(
            filter(() => this.form.controls.nvbKravsEj.value === false),
            tap(() => this.reset()),
            takeUntil(this.ngOnDestroy$)
        ).subscribe();
    }

    get atgard(): Atgard {
        return this._atgard;
    }

    get atgardGuid(): string {
        return this._atgard.guid;
    }

    get isMellerstaSverige(): boolean {
        return this.naturvardesbedomning?.naturvardesomrade?.guid === this.mellerstaSverige;
    }

    get isNorraSverige(): boolean {
        return this.naturvardesbedomning?.naturvardesomrade?.guid === this.norraSverige;
    }

    get isSodraSverige(): boolean {
        return this.naturvardesbedomning?.naturvardesomrade?.guid === this.sodraSverige;
    }

    isNvbRequired() {
        return this.atgard.isLeveransvirke &&
            !this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri);
    }

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    ngOnInit(): void {
        this.kontraktReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            tap(readonly => {
                if (readonly) {
                    this.form.disable({ emitEvent: false });
                }
            })
        );
    }

    nvbKravsEj(): boolean | null {
        if (!this.atgard || !this.naturvardesbedomning) {
            return null;
        }

        if (this.atgard.isLeveransvirke && !this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri)) {
            return null;
        }

        return this.naturvardesbedomning.nvbKravsEj;
    }

    reset(): void {
        this.naturvardesbedomning.utfordDatum = null;
        this.naturvardesbedomning.utfordAv = null;
        this.naturvardesbedomning.nvbKravsEj = null;
    }

    updateDTO(): void {
        if (!this.persisted) {
            this.nvbMediatorService.create(this.naturvardesomradeGuid);
            this.persisted = true;
        }

        this.naturvardesbedomning.edited = true;
        const naturvardesbedomningDTO: AtgardNaturvardesbedomningDTO = _.clone(this.naturvardesbedomning);
        this.nvbMediatorService.updateNvb(naturvardesbedomningDTO);
    }

    protected async cancel() {
        if (this.nvbHasValues()) {
            const res = await firstValueFrom(this.raskModalService.confirm(new ModalSettings("Avbryt NVB/Riskbedömning", "Detta rensar NVB/Riskbedömnings- formuläret på all ifylld data, vill du fortsätta?")));
            if (res === ConfirmDialogResponse.cancel) {
                return;
            }
        }

        this.clearNvbValues();
    }

    protected cancelButtonVisible() {
        if (this.naturvardesbedomning.utfordDatum != null) {
            return false;
        }

        if (this.isNvbRequired()) {
            return ObjectUtils.hasValue(this.naturvardesbedomning.naturvardesbedomningKalla);
        } else if (this.virkeskopService.isForyngringsavverkning(this._atgard.atgardstyp)) {
            return false;
        } else {
            return ObjectUtils.hasValue(this.naturvardesbedomning.nvbKravsEj);
        }
    }

    protected continue() {
        const { nvbKravsEj, nvbSource } = this.form.value;
        // Feature on
        if (this.isNvbRequired()) {
            if (nvbSource === "Formulär") {
                this.reset();
            }
            if (nvbSource === "Riskbedömning") {
                this.reset();
            }
        } else {
            if (nvbKravsEj) {
                // Endast för att sätta i frontend
                this.naturvardesbedomning.utfordDatum = DateUtils.shortDateString(DateUtils.now());
                this.naturvardesbedomning.utfordAv = this.authService.getUserData().fullUserName;
            } else {
                // Rensa i frontend
                this.naturvardesbedomning.utfordDatum = null;
                this.naturvardesbedomning.utfordAv = null;
            }
        }
        this.naturvardesbedomning.naturvardesbedomningKalla = nvbSource;
        this.naturvardesbedomning.nvbKravsEj = nvbKravsEj;

        this.updateDTO();
    }

    protected onFileUpload() {
        this.naturvardesbedomning.utfordDatum = DateUtils.shortDateString(DateUtils.now());
        this.naturvardesbedomning.utfordAv = this.authService.getUserData().fullUserName;
        this.naturvardesbedomning.naturvardesbedomningUtford = true;
        this.updateDTO();
    }

    protected showForm(): void {
        if (this.naturvardesbedomning?.naturvardesomrade || ObjectUtils.isNullOrUndefined(this.atgard.centrumKoordinat)) {
            return;
        }
        this.naturvardesomradeNotFound = false;
        // Gör anrop och fråga efter formulärtyp, kör sedan vidare
        this.avtalService.getAtgardNvbOmrade(this._atgard.centrumKoordinat).subscribe(p => {
            if (ObjectUtils.isNullOrUndefined(p)) {
                this.naturvardesomradeNotFound = true;
            } else {
                this.naturvardesomradeGuid = p.guid;
                this.naturvardesbedomning.naturvardesomrade = new NaturvardesomradeDTO();
                this.naturvardesbedomning.naturvardesomrade.guid = p.guid;
            }

            if (this.virkeskopService.isForyngringsavverkning(this._atgard.atgardstyp)) {
                this.naturvardesbedomning.nvbKravsEj = false;
                this.naturvardesbedomning.utfordDatum = null;
                this.naturvardesbedomning.utfordAv = null;
                this.naturvardesbedomning.edited = true;
            }

            if (!this.naturvardesbedomning.utfordDatum) {
                this.handleExistingDocuments();
            }
        });
    }

    private clearNvbValues(): void {
        const propertysToPersist: string[] = [
            "edited",
            "atgardId",
            "naturvardesomrade",
            "id"
        ];

        Object.keys(this.naturvardesbedomning)
            .forEach((i) => {
                if (!propertysToPersist.includes(i)) {
                    this.naturvardesbedomning[i] = null;
                }
            });
        this.form.reset();
        this.updateDTO();
    }

    private async handleExistingDocuments() {
        const dokument = await this.kontraktDokumentService.getDokumentForAtgard(this.atgard.id, this.blobStorageDokumentTypNvbBilaga);
        if (dokument.length > 0) {
            // Är en fil uppladdad så bör man bli låst till det valet
            this.naturvardesbedomning.naturvardesbedomningKalla = "Fil";
            this.onFileUpload();
        }
    }

    private init() {
        this.requireAtLeastOneFile = this._atgard.isTillgangligt;

        this.avtalService.getNaturvardesBedomning(this._atgard.id).subscribe(a => {
            this.naturvardesbedomning = a;
            if (!ObjectUtils.isNullOrUndefined(this.naturvardesbedomning)) {
                this.persisted = true;
            } else {
                this.naturvardesbedomning = new AtgardNaturvardesbedomningDTO();
            }

            this.form.patchValue({
                nvbKravsEj: this.naturvardesbedomning.nvbKravsEj,
                nvbSource: this.naturvardesbedomning.naturvardesbedomningKalla
            }, {
                emitEvent: false,
                onlySelf: true
            });

            this.showForm();
        });
    }

    private nvbHasValues(): boolean {
        let hasNvbValues = false;

        const propertysExclude: string[] = [
            "edited",
            "atgardId",
            "naturvardesomrade",
            "id",
            "nvbKravsEj",
            "utfordAv",
            "utfordDatum",
            "naturvardesbedomningUtford",
            "naturvardesbedomningSource"
        ];

        for (const i of Object.keys(this.naturvardesbedomning)) {
            if (!_.isNil(this.naturvardesbedomning[i]) && !propertysExclude.includes(i)) {
                hasNvbValues = true;
                break;
            }
        }

        return hasNvbValues;
    }
}
